import React, { useState, useRef, useCallback } from 'react';
import {
  Camera, Upload, X, AlertCircle,
  Loader, FileImage, RotateCcw
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.synfin.ca';

const ReceiptUploader = ({ onReceiptProcessed, onCancel }) => {
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [cameraActive, setCameraActive] = useState(false);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    setError('');
    setImageFile(file);

    // Use FileReader for reliable cross-browser/mobile image preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 } }
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
    } catch {
      setError('Could not access camera. Please use file upload instead.');
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setImageFile(file);
      // Use FileReader for consistent preview across all devices
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(blob);
      stopCamera();
    }, 'image/jpeg', 0.95);
  };

  const reset = () => {
    stopCamera();
    setImage(null);
    setImageFile(null);
    setError('');
    setProgress('');
  };

  const processReceipt = async () => {
    if (!imageFile) return;
    setProcessing(true);
    setError('');

    try {
      setProgress('Connecting to server...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Please log in again to scan receipts.');
      }

      setProgress('Uploading receipt...');
      const formData = new FormData();
      formData.append('receipt', imageFile);

      setProgress('Scanning receipt with AI...');
      const response = await fetch(`${API_URL}/api/receipts/scan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to scan receipt');

      setProgress('Done!');
      onReceiptProcessed({ ...result.data, imageFile });

    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message || 'Failed to process receipt. Please try again.');
    }
    setProcessing(false);
  };

  return (
    <div className="uploader-container">
      <div className="uploader-header">
        <h2 className="uploader-title">Upload Receipt</h2>
        <button className="uploader-close" onClick={onCancel}><X size={20} /></button>
      </div>

      {error && (
        <div className="uploader-error"><AlertCircle size={16} />{error}</div>
      )}

      {!image && !cameraActive && (
        <div className="uploader-options">
          <p className="uploader-subtitle">Choose how to add your receipt</p>
          <div className="uploader-option-grid">
            <button className="uploader-option-btn" onClick={startCamera}>
              <div className="uploader-option-icon"><Camera size={32} /></div>
              <p className="uploader-option-label">Take Photo</p>
              <p className="uploader-option-sub">Use your camera</p>
            </button>
            <button className="uploader-option-btn"
              onClick={() => fileInputRef.current?.click()}>
              <div className="uploader-option-icon"><Upload size={32} /></div>
              <p className="uploader-option-label">Upload File</p>
              <p className="uploader-option-sub">From your device</p>
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*"
            onChange={handleFileSelect} style={{ display: 'none' }} />
        </div>
      )}

      {cameraActive && !image && (
        <div className="camera-container">
          <video ref={videoRef} autoPlay playsInline className="camera-video" />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div className="camera-overlay"><div className="camera-frame" /></div>
          <div className="camera-controls">
            <button className="camera-cancel-btn" onClick={reset}>
              <X size={20} /> Cancel
            </button>
            <button className="camera-capture-btn" onClick={capturePhoto}>
              <Camera size={24} />
            </button>
            <div style={{ width: 80 }} />
          </div>
        </div>
      )}

      {image && !processing && (
        <div className="preview-container">
          <div className="preview-image-wrapper">
            <img src={image} alt="Receipt" className="preview-image" />
          </div>
          <div className="preview-actions">
            <button className="preview-retry-btn" onClick={reset}>
              <RotateCcw size={16} /> Retake / Change
            </button>
            <button className="preview-process-btn" onClick={processReceipt}>
              <FileImage size={16} /> Scan Receipt
            </button>
          </div>
        </div>
      )}

      {processing && (
        <div className="processing-container">
          <div className="processing-spinner">
            <Loader size={40} className="spin" />
          </div>
          <p className="processing-label">{progress}</p>
          <p className="processing-sub">
            Powered by Mindee AI — usually takes 3–8 seconds.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReceiptUploader;
